import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { limit: 20, ordering: "name" };

const state = {
    providers: [],
    filters: DEFAULT_FILTERS,
    providers_loading: false,
    providers_count: 0,
    providers_loading_errors: null,
    providers_cancel_source: null,
    synchronizing_providers: false,
    synchronizing_providers_errors: null,

    // Bulk actions state
    selected_items: [],
    bulk_action_loading: false,
};

const mutations = {
    updateFilters(state, filters) {
        state.filters = filters;
        saveFiltersToStorage(getStorageKey('providers', 'default'), state.filters);
    },
    updateProviders(state, providers) {
        state.providers = providers;
        // Replace selected_items instances with newly fetched providers based on id field
        state.selected_items = state.selected_items?.map((selected_item) => {
            return providers.find((provider) => provider.id === selected_item.id) || selected_item;
        }) || [];
    },
    updateProvidersCount(state, providers_count) {
        state.providers_count = providers_count;
    },
    updateProvidersLoading(state, loading) {
        state.providers_loading = loading;
    },
    updateProvidersLoadingErrors(state, errors) {
        state.providers_loading_errors = errors;
    },
    updateSynchronizingProviders(state, synchronizing) {
        state.synchronize_providers = synchronizing;
    },
    updateSynchronizingProvidersErrors(state, errors) {
        state.synchronizing_providers_errors = errors;
    },
    updateProvidersCancelSource(state, cancel_source) {
        state.providers_cancel_source = cancel_source;
    },
    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items || [];
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};

const actions = {

    exportProvidersAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/providers/",
            params: filters,
        }, $utils.getFilename('providers'));
    },

    exportProductsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        filters.format = 'csv';
        filters.limit = 20000;
        filters.provider_connected = false;
        
        const providerName = params?.provider_name || 'all';
        
        $utils.downloadFile({
            url: "/api/providercatalogentries/",
            params: filters,
        }, $utils.getFilename('providerCatalogEntries'));
    },

    async synchronizeProviders({ commit, dispatch, state }, params) {
        commit('updateSynchronizingProviders', true);
        commit('updateSynchronizingProvidersErrors', null);

        let url = '/api/sync/providers/';

        try {
            const response = await axios.post(url, { payload: params.payload });
            commit('updateSynchronizingProviders', false);
            return response.data.payload;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            error.details = error.details.payload;
            commit('updateSynchronizingProviders', false);
            commit('updateSynchronizingProvidersErrors', error.details);
            throw error;
        }
    },

    async fetchProviders({ commit, dispatch, state }, params) {
        commit('updateProvidersLoading', true);
        commit('updateProvidersLoadingErrors', null);

        if (state.providers_cancel_source) {
            state.providers_cancel_source.cancel("canceled");
        }

        const filters = loadFiltersFromStorage(getStorageKey('providers', 'default'), DEFAULT_FILTERS);
        commit('updateFilters', filters);

        const cancelSource = axios.CancelToken.source();
        commit('updateProvidersCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/providers/', {
                params: filters,
                cancelToken: state.providers_cancel_source?.token
            });
            commit('updateProvidersLoading', false);
            commit('updateProviders', response.data.results);
            commit('updateProvidersCount', response.data.count);
            commit('updateProvidersCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            let error = utils.handleError(xhr_error);
            commit('updateProvidersLoadingErrors', error.details);
            commit('updateProvidersLoading', false);
            throw error;
        }
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/providers/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch("fetchProviders");
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
